import React from 'react'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Typography,
  Box,
  IconButton,
  Tooltip
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'

// Shared column definition for trawl-biology rows. Exported so the map-level
// "Export Displayed (CSV)" results bar can reuse it.
export const TRAWL_BIOLOGY_COLUMNS = [
  { label: 'Station ID', key: 'SEAMAPSTATION' },
  { label: 'Cruise No.', key: 'CRUISENO' },
  { label: 'Date', key: 'SAMPLE_DATE' },
  { label: 'Genus', key: 'GENUS' },
  { label: 'Species', key: 'SPECIES' },
  { label: 'Count', key: 'COUNT' },
  { label: 'Total Weight (kg)', key: 'TOTALWEIGHT' },
  { label: 'Valid Swept Area', key: 'valid_swept_area' },
  { label: 'Tow Distance (nmi)', key: 'tow_distance_nmi' },
  { label: 'Effective Width (ft)', key: 'gear_width_ft' },
  { label: 'Swept Area (hectares)', key: 'swept_area_hectares' },
  { label: 'Swept Area Method', key: 'swept_area_method' },
  { label: 'Swept Area QA', key: 'swept_area_warning' },
  { label: 'Season', key: 'CRUISETITLE' }
]

const HIDDEN_DETAIL_KEYS = new Set(['swept_area_method', 'swept_area_warning'])
const TRAWL_BIOLOGY_DETAIL_COLUMNS = TRAWL_BIOLOGY_COLUMNS.filter(col => !HIDDEN_DETAIL_KEYS.has(col.key))

// `data` is the currently selected station's biology rows shown in the table
//   (shape: { primary: [...], individuals: [...] } or a flat array).
// The per-point CSV buttons (primary aggregate + individual specimens) were
// removed in favor of the always-visible ResultsExportBar at the top of the
// table panel; that bar covers all displayed rows.
export default function PointDataTableTrawlBiology({ data }) {
  // If no data is selected yet, show nothing
  if (!data || ((!data.primary || !Array.isArray(data.primary) || data.primary.length === 0) &&
                (!Array.isArray(data) || data.length === 0))) {
    return null
  }

  // Handle either the new data structure or the old one
  const primaryData = Array.isArray(data) ? data : data.primary;
  const individualData = data.individuals || [];

  const columns = TRAWL_BIOLOGY_DETAIL_COLUMNS

  // Define columns for individual specimens
  const individualColumns = [
    { label: 'Station ID', key: 'SEAMAPSTATION' },
    { label: 'Cruise No.', key: 'CRUISENO' },
    { label: 'Date', key: 'SAMPLE_DATE' },
    { label: 'Genus', key: 'GENUS' },
    { label: 'Species', key: 'SPECIES' },
    { label: 'Individual Wt. (kg)', key: 'INDVL_WT' },
    { label: 'Length (mm)', key: 'LEN_GLF' },
    { label: 'Sex', key: 'SEX_GLF' },
    { label: 'Maturity', key: 'MAT_GLF' },
    { label: 'Season', key: 'CRUISETITLE' }
  ]

  // Format data as needed - copy to avoid modifying the original data
  const formattedData = primaryData.map(item => ({ ...item }));

  return (
    <Box sx={{
      width: '100%',
      paddingLeft: { xs: '0.5rem', sm: '1rem' },
      paddingRight: { xs: '0.5rem', sm: '1rem' }
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: { xs: 1, sm: 2 }
      }}>
        <Typography variant="h6" sx={{
          color: '#2E5A88',
          fontSize: { xs: '0.9rem', sm: '1rem' },
          marginRight: 1
        }}>
          Trawl Biology Station Aggregate
        </Typography>
        <Tooltip
          title="All specimens caught at this station for this taxon in total."
          placement="top"
          arrow
        >
          <IconButton
            size="small"
            sx={{
              padding: '2px',
              color: '#2E5A88',
              '&:hover': {
                backgroundColor: 'rgba(46, 90, 136, 0.1)'
              }
            }}
          >
            <InfoIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Primary Table - BGSREC data */}
      <TableContainer
        component={Paper}
        elevation={2}
        sx={{
          width: '100%',
          overflowX: 'auto', // Enable horizontal scrolling
          overflowY: 'visible',
          marginBottom: 3,
          maxHeight: 'none' // Remove height restrictions
        }}
      >
        <Table sx={{
          width: '100%',
          minWidth: { xs: '1000px', md: '100%' } // Minimum width for mobile scrolling
        }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #ccc',
                    color: '#1a2a3a',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '8px', sm: '16px' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {formattedData.map((row, index) => (
              <TableRow
                key={index}
                sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } }}
              >
                {columns.map((col) => {
                  let cellValue = row[col.key];

                  if (typeof cellValue === 'boolean') {
                    cellValue = cellValue ? 'Yes' : 'No';
                  }

                  // Format numeric values for count and weight
                  if (['COUNT', 'TOTALWEIGHT'].includes(col.key)) {
                    if (!isNaN(cellValue) && cellValue !== null && cellValue !== '') {
                      // Format COUNT as whole number
                      if (col.key === 'COUNT') {
                        cellValue = Math.round(Number(cellValue));
                      } else {
                        // Format TOTALWEIGHT with one decimal place
                        cellValue = Number(cellValue).toFixed(1);
                      }
                    }
                  }

                  if (['tow_distance_nmi', 'gear_width_ft'].includes(col.key)) {
                    if (!isNaN(cellValue) && cellValue !== null && cellValue !== '') {
                      cellValue = Number(cellValue).toFixed(2);
                    }
                  }

                  if (col.key === 'swept_area_hectares') {
                    if (!isNaN(cellValue) && cellValue !== null && cellValue !== '') {
                      cellValue = Number(cellValue).toFixed(2);
                    }
                  }

                  return (
                    <TableCell
                      key={`${index}-${col.key}`}
                                              sx={{
                          color: index % 2 === 0 ? '#333333' : 'white',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          padding: { xs: '8px', sm: '16px' },
                          whiteSpace: 'nowrap'
                        }}
                    >
                      {cellValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Only show individual specimens section if there are specimens */}
      {individualData.length > 0 && (
        <>
          {/* Divider with title for individual specimens section */}
          <Box sx={{
            mb: 2,
            mt: 4,
            width: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{
                color: '#2E5A88',
                fontWeight: 600,
                fontSize: '1rem',
                textAlign: 'left',
                marginRight: 1
              }}
            >
              Select Specimens
            </Typography>
            <Tooltip
              title="Selected specimens taken as a subsample from the station total for the given taxon."
              placement="top"
              arrow
            >
              <IconButton
                size="small"
                sx={{
                  padding: '2px',
                  color: '#2E5A88',
                  '&:hover': {
                    backgroundColor: 'rgba(46, 90, 136, 0.1)'
                  }
                }}
              >
                <InfoIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Secondary Table - GLFREC data (individual specimens) */}
          <TableContainer
            component={Paper}
            elevation={2}
            sx={{
              width: '100%',
              overflowX: 'visible',
              overflowY: 'visible'
            }}
          >
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  {individualColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        borderBottom: '2px solid #ccc',
                        color: '#1a2a3a'
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {individualData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } }}
                  >
                    {individualColumns.map((col) => {
                      let cellValue = row[col.key];

                      // Format numeric values
                      if (col.key === 'INDVL_WT') {
                        // For individual weight: display blank if 0 or 0.0
                        if (cellValue === 0 || cellValue === '0' || cellValue === 0.0 || cellValue === '0.0') {
                          cellValue = '';
                        } else if (!isNaN(cellValue) && cellValue !== null && cellValue !== '') {
                          // Format with three decimal places
                          cellValue = Number(cellValue).toFixed(3);
                        }
                      } else if (col.key === 'LEN_GLF') {
                        // For length: display as integer
                        if (!isNaN(cellValue) && cellValue !== null && cellValue !== '') {
                          // Format as integer with no decimal places
                          cellValue = Math.round(Number(cellValue));
                        }
                      }

                      return (
                        <TableCell
                          key={`${index}-${col.key}`}
                                                  sx={{
                          color: index % 2 === 0 ? '#333333' : 'white',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          padding: { xs: '8px', sm: '16px' },
                          whiteSpace: 'nowrap'
                        }}
                        >
                          {cellValue}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  )
}
